// ═══════════════════════════════════════════════════════════════
// AddressAutocomplete — Reusable address input with HERE geocoding
// Works with sp-* mobile CSS classes or custom className/style.
// ═══════════════════════════════════════════════════════════════

import { useState, useRef } from 'react';
import { searchAddress, type AddressResult } from '../../utils/perimeterOverpass';

interface Props {
  value: string;
  onChange: (value: string, lat?: number, lng?: number) => void;
  placeholder?: string;
  /** CSS class applied to the <input>. Defaults to "sp-input" for mobile portal. */
  inputClassName?: string;
  label?: string;
  /** Show required asterisk */
  required?: boolean;
  /** Additional wrapper style */
  wrapStyle?: React.CSSProperties;
  disabled?: boolean;
}

export function AddressAutocomplete({
  value,
  onChange,
  placeholder = 'Street address…',
  inputClassName = 'sp-input',
  label,
  required,
  wrapStyle,
  disabled,
}: Props) {
  const [results, setResults] = useState<AddressResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleInput = (val: string) => {
    onChange(val);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (val.trim().length < 3) { setResults([]); setShowResults(false); return; }
    timeoutRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await searchAddress(val.trim());
        setResults(res);
        setShowResults(res.length > 0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 400);
  };

  const handleSelect = (r: AddressResult) => {
    onChange(r.display_name, parseFloat(r.lat), parseFloat(r.lon));
    setResults([]);
    setShowResults(false);
  };

  const handleBlur = () => {
    // Small delay so click on result registers first
    setTimeout(() => setShowResults(false), 150);
  };

  return (
    <div style={{ position: 'relative', ...wrapStyle }}>
      {label && (
        <label className="sp-label">
          {label}
          {required && <span className="sp-req"> *</span>}
        </label>
      )}
      <input
        className={inputClassName}
        value={value}
        onChange={e => handleInput(e.target.value)}
        onBlur={handleBlur}
        onFocus={() => results.length > 0 && setShowResults(true)}
        placeholder={placeholder}
        autoComplete="off"
        disabled={disabled}
      />
      {loading && (
        <div style={{
          position: 'absolute', right: 10, top: label ? 36 : 10,
          fontSize: 12, color: '#939598',
        }}>
          Searching…
        </div>
      )}
      {showResults && results.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 4,
          boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)', maxHeight: 220, overflowY: 'auto',
          marginTop: 2,
        }}>
          {results.map((r, i) => (
            <div
              key={r.place_id + i}
              onMouseDown={e => { e.preventDefault(); handleSelect(r); }}
              style={{
                padding: '11px 16px', cursor: 'pointer', fontSize: 14, color: '#1B1C1C',
                borderBottom: i < results.length - 1 ? '1px solid #f1f5f9' : 'none',
                lineHeight: 1.4,
              }}
              onMouseEnter={e => (e.currentTarget.style.background = '#F8F8F8')}
              onMouseLeave={e => (e.currentTarget.style.background = '')}
            >
              {r.display_name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
